import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import useHabitat from '../../../hooks/useHabitat';
import Content from '../Content';
import { Discipline } from '../../../core/typings';
import './styles.scss';

interface AccordionPanelProps {
  index: number;
  activeTab: boolean;
  toggleTab: Function;
  label: string | JSX.Element;
  content: string | JSX.Element | JSX.Element[];
  themeColor: string;
  discipline?: string;
  isAsync?: boolean;
  isResults?: boolean;
}

const AccordionPanel = ({
  index,
  activeTab,
  toggleTab,
  label,
  content,
  themeColor,
  discipline,
  isAsync,
  isResults,
}: AccordionPanelProps) => {
  const [height, setHeight] = useState<string>('0px');
  const [asyncContentLoaded, setAsyncContentLoaded] = useState<boolean>(false);
  const wrapper = useRef<HTMLDivElement>(null);

  const calculatePanelHeight = () => {
    const panelElement = wrapper.current as HTMLDivElement;

    if (activeTab) {
      panelElement.ontransitionend = () => {
        setHeight('auto');
      };

      setHeight(`${panelElement.scrollHeight}px`);
    } else {
      panelElement.ontransitionend = null;

      setHeight(`${panelElement.scrollHeight}px`);

      requestAnimationFrame(() => {
        setHeight('0px');
      });
    }
  };

  const handleLoadedContent = () => {
    setAsyncContentLoaded(true);
    calculatePanelHeight();
  };

  useEffect(() => {
    calculatePanelHeight();
  }, [activeTab]);

  return (
    <div className={`panel panel--${themeColor} ${isResults ? 'panel--results' : ''} ${isResults && activeTab ? 'panel--results__open' : ''}`}>
      <button
        type="button"
        className={`panel__label ${
          discipline ? `panel__label--${discipline}` : ''
        }`}
        aria-expanded={activeTab}
        onClick={() => toggleTab(index)}
      >
        {label}
      </button>
      <div
        ref={wrapper}
        className="panel__inner"
        aria-hidden={!activeTab}
        style={{
          height: `${height}`,
        }}
      >
        {typeof content === 'string' && (
          <div className="panel__content">
            <Content content={content} containerTheme={themeColor} />
          </div>
        )}

        {!isAsync && React.isValidElement(content) && content}

        {isAsync && (activeTab || asyncContentLoaded) && React.isValidElement(content) && (
          <content.type
            {...content.props}
            onLoad={handleLoadedContent}
            onBeforeLoad={calculatePanelHeight}
          />
        )}
      </div>
    </div>
  );
};

export interface AccordionPanelType {
  label: string | JSX.Element;
  content: string | JSX.Element | JSX.Element[];
}

interface AccordionProps {
  panels: AccordionPanelType[];
  themeColor: string;
  discipline?: Discipline;
  isResults?: boolean;
}

interface PanelsState {
  label: string | JSX.Element;
  content: string | JSX.Element | JSX.Element[];
  activeTab: boolean;
}

const Accordion = ({
  panels,
  themeColor,
  discipline,
  isResults,
}: AccordionProps) => {
  const [panelsInState, setPanelsInState] = useState<PanelsState[]>([]);
  useHabitat();

  const toggleTab = (index: number) => {
    const current: PanelsState[] = [...panelsInState];

    current[index].activeTab = !panelsInState[index].activeTab;
    setPanelsInState(current);
  };

  useEffect(() => {
    if (panels) {
      const mappedPanels: PanelsState[] = panels.map((panel) => ({
        ...panel,
        activeTab: false,
      }));

      setPanelsInState(mappedPanels);
    }
  }, []);

  return (
    <div className="accordion">
      {panelsInState && (
        <>
          {panelsInState.map((panel, index) => (
            <AccordionPanel
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              toggleTab={toggleTab}
              index={index}
              themeColor={themeColor}
              discipline={discipline}
              isResults={isResults}
              {...panel}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default Accordion;
